import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ShieldAlert, AlertTriangle, Play, ShieldCheck, FileX, Maximize } from 'lucide-react';
import toast from 'react-hot-toast';
import QuizInterface from '../../components/quiz/QuizInterface';
import { API_CONFIG } from '../../config/api.config';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const OnlineExamInterface: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [examData, setExamData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // States cho Anti-cheat
  const [examStarted, setExamStarted] = useState(false);
  const [examSubmitted, setExamSubmitted] = useState(false);
  const [tabSwitchCount, setTabSwitchCount] = useState(0);
  // Mobile browsers (iOS Safari, Zalo in-app, Android in-app) không hỗ trợ Fullscreen API
  // → khởi tạo isFullscreen = true để bypass overlay chặn thi trên mobile
  const [isFullscreen, setIsFullscreen] = useState(() => !document.fullscreenEnabled);
  // Chỉ cảnh báo "thoát toàn màn hình" SAU KHI đã thực sự vào fullscreen ít nhất 1 lần.
  // Tránh báo sai khi fullscreen chưa từng bật (desktop từ chối / container chưa mount).
  const [enteredFullscreen, setEnteredFullscreen] = useState(false);
  const examContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchExam = async () => {
      try {
        const response = await fetch(`${API_CONFIG.python.apiUrl}/pmaker-advanced/exam/${slug}`);
        if (!response.ok) {
          throw new Error('Không tìm thấy bài thi hoặc bài thi chưa được mở.');
        }
        const result = await response.json();
        if (result.success && result.data) {
          const data = result.data;

          // --- KIỂM TRA THỜI GIAN MỞ/ĐÓNG ĐỀ ---
          const now = new Date();
          if (data.settings?.start_time) {
            const startTime = new Date(data.settings.start_time);
            if (now < startTime) throw new Error(`Bài thi chưa mở. Thời gian mở: ${startTime.toLocaleString()}`);
          }
          if (data.settings?.end_time) {
            const endTime = new Date(data.settings.end_time);
            if (now > endTime) throw new Error(`Bài thi đã kết thúc vào: ${endTime.toLocaleString()}`);
          }

          // --- KIỂM TRA SỐ LƯỢT THI ---
          if (data.settings?.max_attempts && data.settings.max_attempts > 0) {
            const token = localStorage.getItem('jwt_token');
            if (!token) throw new Error('Vui lòng đăng nhập để kiểm tra số lượt thi của bạn.');
            
            const attemptRes = await fetch(`${API_CONFIG.python.apiUrl}/pmaker-advanced/check-attempts/${data.id}`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            if (attemptRes.ok) {
              const attemptData = await attemptRes.json();
              if (attemptData.data && !attemptData.data.can_attempt) {
                throw new Error(`Bạn đã hết lượt làm bài (Đã thi ${attemptData.data.attempts_count}/${attemptData.data.max_attempts} lượt).`);
              }
            }
          }

          // Transform data format cho QuizInterface
          const questions = data.parts?.flatMap((p: any) => p.questions.map((q: any) => {
            // Lưu lại part title và description vào câu hỏi
            q.partTitle = p.title;
            q.partDescription = p.intro_markdown;

            // Đối với câu hỏi Đúng/Sai (dung_sai), Backend PMaker lưu nội dung ở cau_hoi
            // nhưng Ebook TrueFalseQuestion component cần question_lead
            if ((q.type === 'dung_sai' || q.type === 'true_false') && !q.question_lead) {
              q.question_lead = q.cau_hoi;
            }

            // Đảm bảo lua_chon là mảng (SQLite có thể trả về string)
            if (typeof q.lua_chon === 'string') {
              try { 
                let parsed = JSON.parse(q.lua_chon);
                if (Array.isArray(parsed) && parsed.length > 0 && typeof parsed[0] === 'object') {
                  // Trích xuất text từ object {text: "..."}
                  parsed = parsed.map((c: any) => c.text || c.content || JSON.stringify(c));
                }
                q.lua_chon = parsed; 
              } catch(e) {}
            } else if (Array.isArray(q.lua_chon) && q.lua_chon.length > 0 && typeof q.lua_chon[0] === 'object') {
              q.lua_chon = q.lua_chon.map((c: any) => c.text || c.content || JSON.stringify(c));
            }

            if (typeof q.goi_y === 'string') {
              try { q.goi_y = JSON.parse(q.goi_y); } catch(e) {}
            }
            if (typeof q.examMetadata === 'string') {
              try { q.examMetadata = JSON.parse(q.examMetadata); } catch(e) {}
            }
            // Transform dap_an_dung -> dapAnDung cho frontend
            if (q.dap_an_dung) {
                q.dapAn = q.dap_an_dung;
            }
            return q;
          })) || [];
          
          // --- LUẬT THI: XÁO TRỘN ---
          let finalQuestions = [...questions];

          // 1. Shuffle options (Đảo đáp án)
          if (data.settings?.shuffle_options) {
            finalQuestions = finalQuestions.map(q => {
              if (q.type === 'trac_nghiem_nhieu_lua_chon' && Array.isArray(q.lua_chon) && q.lua_chon.length > 0) {
                const correctChar = q.dapAn || q.dap_an_dung;
                let correctIndex = -1;
                if (correctChar === 'A') correctIndex = 0;
                else if (correctChar === 'B') correctIndex = 1;
                else if (correctChar === 'C') correctIndex = 2;
                else if (correctChar === 'D') correctIndex = 3;

                if (correctIndex >= 0 && correctIndex < q.lua_chon.length) {
                  const correctContent = q.lua_chon[correctIndex];
                  
                  const shuffled = [...q.lua_chon];
                  for (let i = shuffled.length - 1; i > 0; i--) {
                    const j = Math.floor(Math.random() * (i + 1));
                    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
                  }
                  
                  q.lua_chon = shuffled;
                  const newIndex = shuffled.indexOf(correctContent);
                  if (newIndex === 0) q.dapAn = 'A';
                  else if (newIndex === 1) q.dapAn = 'B';
                  else if (newIndex === 2) q.dapAn = 'C';
                  else if (newIndex === 3) q.dapAn = 'D';
                  q.dap_an_dung = q.dapAn;
                }
              }
              return q;
            });
          }

          // 2. Shuffle questions (Đảo câu hỏi) — câu chùm cùng cluster_id được xáo trộn như 1 đơn vị
          if (data.settings?.shuffle_questions) {
            // Gom câu chùm thành units, câu đơn mỗi câu là 1 unit
            const units: any[][] = [];
            const clusterUnits = new Map<string, any[]>();
            for (const q of finalQuestions) {
              const cid = q.examMetadata?.cluster_id;
              if (cid) {
                if (!clusterUnits.has(cid)) {
                  const unit: any[] = [];
                  clusterUnits.set(cid, unit);
                  units.push(unit);
                }
                clusterUnits.get(cid)!.push(q);
              } else {
                units.push([q]);
              }
            }
            // Xáo trộn các units
            for (let i = units.length - 1; i > 0; i--) {
              const j = Math.floor(Math.random() * (i + 1));
              [units[i], units[j]] = [units[j], units[i]];
            }
            finalQuestions = units.flat();
          }

          // 3. Phân loại và sắp xếp lại theo thứ tự Nhóm để Ebook có thể render các Phần (Trắc nghiệm -> Đúng/Sai -> Trả lời ngắn)
          const getGroupWeight = (q: any) => {
              const type = q.type || q.loai || '';
              if (type.includes('trac_nghiem') || type.includes('multiple_choice') || q.examMetadata?.group === 'TN') return 1;
              if (type.includes('dung_sai') || type.includes('true_false') || q.examMetadata?.group === 'DS') return 2;
              if (type.includes('tinh_toan') || type.includes('short_answer') || type.includes('tra_loi_ngan') || q.examMetadata?.group === 'TL') return 3;
              return 4; // Tự luận và Khác
          };
          
          // Stable sort by group weight to preserve shuffle order within groups
          finalQuestions.sort((a, b) => getGroupWeight(a) - getGroupWeight(b));
          
          setExamData({
            id: data.id,
            tieuDe: data.title,
            ten: data.title,
            gioi_han_thoi_gian: data.settings?.duration ? data.settings.duration * 60 : 2700, // Sử dụng duration từ pmaker
            cauHoi: finalQuestions,
            settings: data.settings,
            parts: data.parts // Giữ lại parts nếu QuizInterface cần
          });
        } else {
          throw new Error('Dữ liệu bài thi không hợp lệ.');
        }
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchExam();
    }
  }, [slug]);

  // Anti-cheat: Tab switch detection
  useEffect(() => {
    if (!examStarted || examSubmitted || !examData?.settings?.tab_switch_warning) return;

    const handleVisibilityChange = () => {
      if (document.hidden) {
        setTabSwitchCount(prev => {
          const newCount = prev + 1;
          if (newCount >= 3) {
            toast.error('Bạn đã rời khỏi trang quá 3 lần. Bài thi sẽ tự động nộp!', { duration: 5000 });
            // Logic auto submit sẽ được trigger ở QuizInterface
            // Hiện tại ta show cảnh báo lớn
          } else {
            toast.error(`CẢNH BÁO VI PHẠM LẦN ${newCount}: Bạn vừa rời khỏi bài thi! Nếu vi phạm 3 lần bài sẽ tự nộp.`, {
              duration: 5000,
              icon: '🚨'
            });
          }
          return newCount;
        });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [examStarted, examSubmitted, examData]);

  // Anti-cheat: Fullscreen Lock
  useEffect(() => {
    // Trên mobile không hỗ trợ fullscreen API → không đăng ký listener
    if (!document.fullscreenEnabled) return;

    const handleFullscreenChange = () => {
      const isFull = !!document.fullscreenElement;
      setIsFullscreen(isFull);
      if (isFull) setEnteredFullscreen(true);
      if (enteredFullscreen && examStarted && !examSubmitted && examData?.settings?.require_fullscreen && !isFull) {
        toast.error('CẢNH BÁO: Vui lòng bật Toàn màn hình để tiếp tục làm bài!');
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, [examStarted, examData, examSubmitted, enteredFullscreen]);

  // Anti-cheat: Prevent Copy / Right click
  useEffect(() => {
    if (!examData?.settings?.prevent_copy) return;

    const handleContextMenu = (e: MouseEvent) => { e.preventDefault(); };
    const handleCopy = (e: ClipboardEvent) => { e.preventDefault(); toast.error('Hành động sao chép bị vô hiệu hóa!'); };
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === 'c' || e.key === 'v' || e.key === 'p' || e.key === 's')) {
        e.preventDefault();
      }
    };

    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('copy', handleCopy);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('copy', handleCopy);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [examData]);

  const requestFullscreen = async () => {
    // Không hỗ trợ API → bypass (mobile/in-app browser)
    if (!document.fullscreenEnabled) {
      setIsFullscreen(true);
      return;
    }
    if (examContainerRef.current) {
      try {
        await examContainerRef.current.requestFullscreen();
        setIsFullscreen(true);
        setEnteredFullscreen(true);
      } catch (err) {
        // API tồn tại nhưng request bị từ chối (Zalo/Samsung browser...) → bypass luôn
        setIsFullscreen(true);
        toast('Trình duyệt không hỗ trợ toàn màn hình — bài thi vẫn tiếp tục bình thường.', { icon: 'ℹ️' });
      }
    }
  };

  const startExam = async () => {
    if (examData?.settings?.require_fullscreen) {
      await requestFullscreen();
    }
    setExamStarted(true);
  };

  // Dự phòng: nếu vẫn đang trong in-app browser (redirect ở index.html thất bại / iOS không có Chrome)
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const isInAppBrowser = /Zalo|FBAN|FBAV|FB_IAB|FBIOS|Instagram|Messenger|Line\//i.test(ua);
  const openInChrome = () => {
    const target = location.host + location.pathname + (location.search || '');
    if (/Android/i.test(ua)) {
      window.location.href = 'intent://' + target +
        '#Intent;scheme=https;package=com.android.chrome;S.browser_fallback_url=' +
        encodeURIComponent(location.href) + ';end;';
    } else if (/iPhone|iPad|iPod/i.test(ua)) {
      window.location.href = 'googlechromes://' + target;
    }
  };
  const copyExamLink = () => {
    navigator.clipboard?.writeText(location.href);
    toast.success('Đã sao chép link. Mở Chrome rồi dán vào để vào thi.');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !examData) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 p-4 text-center">
        <AlertTriangle className="w-16 h-16 text-red-500 mb-4" />
        <h1 className="text-2xl font-bold text-gray-800 mb-2">Lỗi truy cập</h1>
        <p className="text-gray-600 mb-6">{error || 'Không tìm thấy bài thi'}</p>
        <button onClick={() => navigate('/trac-nghiem')} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
          Quay lại danh sách
        </button>
      </div>
    );
  }

  // Màn hình chờ (Intro)
  if (!examStarted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        
        <div className="max-w-2xl w-full bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
          <div className="bg-blue-600 p-8 text-center text-white">
            <h1 className="text-3xl font-bold mb-2">{examData.tieuDe}</h1>
            <p className="opacity-90">{examData.cauHoi.length} Câu hỏi • {Math.floor(examData.gioi_han_thoi_gian / 60)} Phút</p>
          </div>
          
          <div className="p-8 space-y-6">
            {isInAppBrowser && (
              <div className="bg-red-50 border-2 border-red-300 rounded-xl p-4 text-red-800 text-sm space-y-3">
                <p className="font-bold">⚠️ Bạn đang mở trong Zalo/Facebook. Hãy mở bằng Chrome để vào thi ổn định (đăng nhập & toàn màn hình mới hoạt động).</p>
                <div className="flex flex-col sm:flex-row gap-2">
                  <button onClick={openInChrome} className="flex-1 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg font-bold">
                    Mở bằng Chrome
                  </button>
                  <button onClick={copyExamLink} className="flex-1 py-2.5 bg-white border border-red-300 text-red-700 rounded-lg font-semibold hover:bg-red-100">
                    Sao chép link
                  </button>
                </div>
                <p className="text-xs text-red-600">Nếu nút trên không mở được: bấm dấu <b>⋮</b> (góc phải) → <b>Mở trong trình duyệt</b>.</p>
              </div>
            )}
            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 text-yellow-800 text-sm space-y-2">
              <h3 className="font-bold flex items-center gap-2"><ShieldAlert className="w-5 h-5" /> NỘI QUY PHÒNG THI:</h3>
              <ul className="list-disc pl-6 space-y-1">
                {examData.settings?.require_fullscreen && <li>Bài thi yêu cầu chế độ Toàn màn hình (Fullscreen). Nếu thoát ra sẽ bị cảnh báo.</li>}
                {examData.settings?.tab_switch_warning && <li>Tuyệt đối <b>không chuyển Tab</b> hoặc thu nhỏ trình duyệt. Vi phạm 3 lần hệ thống tự động nộp bài.</li>}
                {examData.settings?.prevent_copy && <li>Các phím tắt sao chép và Click chuột phải đã bị vô hiệu hóa.</li>}
                <li>Thời gian sẽ bắt đầu đếm ngược ngay khi bạn nhấn "Bắt đầu làm bài".</li>
              </ul>
            </div>

            <button 
              onClick={startExam}
              className="w-full py-4 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold text-lg shadow-lg shadow-blue-200 flex items-center justify-center gap-2 transition-transform hover:scale-[1.02]"
            >
              <Play className="w-5 h-5" /> BẮT ĐẦU LÀM BÀI
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Màn hình làm bài (Sử dụng Light Mode - forceDarkMode=false)
  return (
    <div 
      ref={examContainerRef} 
      className="min-h-screen h-screen overflow-y-auto bg-amber-50/30 relative"
    >

      {/* Cảnh báo mất fullscreen */}
      {enteredFullscreen && !isFullscreen && examData.settings?.require_fullscreen && !examSubmitted && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm pointer-events-auto">
          <div className="bg-white p-8 rounded-2xl shadow-2xl max-w-md text-center border-2 border-red-500">
            <Maximize className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-red-600 mb-2">BẠN ĐÃ THOÁT TOÀN MÀN HÌNH</h2>
            <p className="text-gray-700 mb-6">Bạn phải quay lại chế độ Toàn màn hình để tiếp tục làm bài!</p>
            <button 
              onClick={requestFullscreen}
              className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl font-bold shadow-lg"
            >
              Tiếp tục làm bài
            </button>
          </div>
        </div>
      )}

      {/* Render Quiz Interface với Force Light Mode */}
      <div className={enteredFullscreen && !isFullscreen && examData.settings?.require_fullscreen && !examSubmitted ? 'pointer-events-none opacity-0' : ''}>
        <QuizInterface
          quizData={examData}
          lessonId={slug || 'online-exam'}
          onSubmitted={() => setExamSubmitted(true)}
          onBack={() => {
            if (document.fullscreenElement) {
              document.exitFullscreen();
            }
            navigate('/trac-nghiem');
          }} 
          forceDarkMode={false}
          requireFullProfile={false}
        />
      </div>
    </div>
  );
};

export default OnlineExamInterface;
